import { useState } from 'react';
import { Shield, X } from 'lucide-react';
import { CalloutList } from '../shared/GroupedCallout';
import { SectionHeader, EmptyState } from '../shared/InfoGrid';
import { SSRCodeBadge } from '../shared/StatusBadge';
import { getSSRBorderColor, getSSRDescription } from '../utils/statusCodes';
import type { SpecialServiceRequest, Dictionaries } from '../utils/types';

interface SSRsTabProps {
  ssrs: SpecialServiceRequest[];
  dictionaries?: Dictionaries;
}

export function SSRsTab({ ssrs, dictionaries }: SSRsTabProps) {
  const [selectedSSR, setSelectedSSR] = useState<SpecialServiceRequest | null>(null);

  if (!ssrs || ssrs.length === 0) {
    return (
      <EmptyState
        icon={<Shield className="h-6 w-6" />}
        title="No SSRs"
        description="This booking doesn't contain any Special Service Requests"
      />
    );
  }

  const getSSRName = (code: string): string => {
    return dictionaries?.specialServiceRequest?.[code]?.name || getSSRDescription(code);
  };

  return (
    <div className="relative">
      <SectionHeader
        title="Special Service Requests"
        icon={<Shield className="h-5 w-5" />}
        count={ssrs.length}
      />

      <CalloutList
        items={ssrs}
        renderContent={(ssr) => (
          <div className="flex items-start gap-2">
            <SSRCodeBadge code={ssr.code} />
            <span className="flex-1">
              {ssr.freetext || (
                <span className="text-slate-400">
                  {formatSSRDetails(ssr)}
                </span>
              )}
            </span>
          </div>
        )}
        getBorderColor={(ssr) => `border-l-${getSSRBorderColor(ssr.code)}`}
        onItemClick={setSelectedSSR}
      />

      {/* SSR Detail Popover/Modal */}
      {selectedSSR && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
          <div
            className="absolute inset-0"
            onClick={() => setSelectedSSR(null)}
          />
          <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
            <button
              onClick={() => setSelectedSSR(null)}
              className="absolute right-4 top-4 p-1 rounded hover:bg-slate-100"
            >
              <X className="h-5 w-5 text-slate-400" />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <SSRCodeBadge code={selectedSSR.code} className="text-sm px-2 py-1" />
              <span className="text-lg font-semibold text-slate-900">
                {getSSRName(selectedSSR.code)}
              </span>
            </div>

            <div className="space-y-3 text-sm">
              <DetailRow label="ID" value={selectedSSR.id} monospace />
              <DetailRow label="Code" value={selectedSSR.code} monospace />
              <DetailRow label="Airline" value={selectedSSR.airlineCode} />
              {selectedSSR.statusCode && (
                <DetailRow label="Status" value={selectedSSR.statusCode} />
              )}
              {selectedSSR.quantity && (
                <DetailRow label="Quantity" value={selectedSSR.quantity.toString()} />
              )}
              {selectedSSR.freetext && (
                <DetailRow label="Free Text" value={selectedSSR.freetext} monospace />
              )}
              {selectedSSR.travelerIds && selectedSSR.travelerIds.length > 0 && (
                <DetailRow label="Travelers" value={selectedSSR.travelerIds.join(', ')} />
              )}
              {selectedSSR.flightIds && selectedSSR.flightIds.length > 0 && (
                <DetailRow label="Flights" value={selectedSSR.flightIds.join(', ')} />
              )}
              {selectedSSR.isVerified !== undefined && (
                <DetailRow label="Verified" value={selectedSSR.isVerified ? 'Yes' : 'No'} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value, monospace }: { label: string; value: string; monospace?: boolean }) {
  return (
    <div className="flex items-start justify-between border-b border-slate-100 pb-2">
      <span className="text-slate-500">{label}</span>
      <span className={`text-slate-900 font-medium text-right max-w-xs break-all ${monospace ? 'font-mono text-xs' : ''}`}>
        {value}
      </span>
    </div>
  );
}

function formatSSRDetails(ssr: SpecialServiceRequest): string {
  const parts: string[] = [];
  if (ssr.airlineCode) parts.push(ssr.airlineCode);
  if (ssr.statusCode) parts.push(ssr.statusCode);
  if (ssr.quantity) parts.push(`x${ssr.quantity}`);
  if (ssr.travelerIds?.length) parts.push(`Pax: ${ssr.travelerIds.join(', ')}`);
  if (ssr.flightIds?.length) parts.push(`Flt: ${ssr.flightIds.join(', ')}`);
  return parts.join(' • ') || 'Click for details';
}
